/**
 * Self-test suite for the receipts module.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/ReceiptSelfTest.swift`.
 *
 * Coverage:
 *
 *   A. Receipt codec — pure (de)serialisation, server-spoofing resistance:
 *      A1. encode → decode round-trips delivered receipts
 *      A2. encode → decode round-trips read receipts
 *      A3. isReceipt is true for our format and false for arbitrary text
 *      A4. tryDecode returns null for non-receipt plaintext
 *      A5. malformed base64 in receipt prefix throws .malformed
 *      A6. wrong schema version throws .unsupportedSchema
 *      A7. empty messageId throws .malformed
 *      A8. tampered ts byte still decodes (ts is plaintext-content; integrity
 *          is enforced by the outer signed E2EE envelope, not the receipt)
 *
 *   B. ReceiptStore — privacy and monotonicity guarantees:
 *      B1. record(delivered) → readBy empty, deliveredBy contains user
 *      B2. record(read) upgrades from delivered, deliveredBy includes
 *          read-by users (read implies delivered)
 *      B3. read receipt does NOT downgrade to delivered if a stale delivered
 *          arrives later
 *      B4. duplicate identical receipt is a no-op (idempotent)
 *      B5. multiple users tracked independently per message
 *      B6. clearing one message does not affect others
 *      B7. persistence: state survives a fresh store instance (shared IDB)
 *      B8. read-receipts-disabled toggle round-trips through storage
 *      B9. observedAt is the local clock, not the peer's claimed ts
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  ReceiptCodec,
  ReceiptCodecError,
  RECEIPT_PREFIX,
  makeReceipt,
} from "./Receipt";
import {
  ReceiptStore,
  deliveredBy,
  readBy,
  isEmptyMessageReceipts,
} from "./ReceiptStore";
import { createStore } from "idb-keyval";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

/** Create a fully isolated ReceiptStore: unique IDB + in-memory toggle storage. */
function freshStore(): ReceiptStore {
  const uniqueDbName = `fr.kaether.receipts.test.${crypto.randomUUID()}`;
  return new ReceiptStore({
    idbStore: createStore(uniqueDbName, "receipts"),
    toggleStorage: makeIsolatedToggleStorage(),
  });
}

/** A minimal in-memory localStorage-shaped toggle backend. */
function makeIsolatedToggleStorage(): Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
> {
  const map = new Map<string, string>();
  return {
    getItem: (k: string) => map.get(k) ?? null,
    setItem: (k: string, v: string) => map.set(k, v),
    removeItem: (k: string) => map.delete(k),
  };
}

// ---------------------------------------------------------------------------
// A. Codec cases
// ---------------------------------------------------------------------------

async function testCodecRoundTripDelivered(): Promise<TestCase> {
  const name = "codec round-trips delivered receipt";
  const r = makeReceipt("msg-123", "delivered", 1_700_000_000);
  const encoded = ReceiptCodec.encode(r);
  try {
    const decoded = ReceiptCodec.decode(encoded);
    const ok =
      decoded.messageId === r.messageId &&
      decoded.type === r.type &&
      decoded.ts === r.ts &&
      decoded.schema === r.schema;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(decoded)}, want ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCodecRoundTripRead(): Promise<TestCase> {
  const name = "codec round-trips read receipt";
  const r = makeReceipt("msg-456", "read", 1_700_000_001);
  const encoded = ReceiptCodec.encode(r);
  try {
    const decoded = ReceiptCodec.decode(encoded);
    const ok =
      decoded.messageId === r.messageId &&
      decoded.type === "read" &&
      decoded.ts === r.ts &&
      decoded.schema === r.schema;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(decoded)}, want ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsReceiptDetection(): Promise<TestCase> {
  const name = "isReceipt distinguishes receipt from user/envelope text";
  const r = makeReceipt("abc", "delivered");
  const encoded = ReceiptCodec.encode(r);
  const positive = ReceiptCodec.isReceipt(encoded);
  const negativeUserMessage = ReceiptCodec.isReceipt("hello world");
  const negativeEnvelope = ReceiptCodec.isReceipt("kaether:e2ee:v1:foo");
  const ok = positive && !negativeUserMessage && !negativeEnvelope;
  return ok
    ? pass(name)
    : fail(name, `pos=${positive} negUser=${negativeUserMessage} negEnv=${negativeEnvelope}`);
}

async function testTryDecodeReturnsNullForUserMessage(): Promise<TestCase> {
  const name = "tryDecode returns null for user messages";
  try {
    const r = ReceiptCodec.tryDecode("just a chat message");
    const ok = r === null;
    return ok ? pass(name) : fail(name, `got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, `threw: ${String(e)}`);
  }
}

async function testMalformedBase64Rejected(): Promise<TestCase> {
  const name = "malformed base64 in receipt is rejected";
  const bad = RECEIPT_PREFIX + "@@@not-base64@@@";
  try {
    ReceiptCodec.decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof ReceiptCodecError && e.tag.kind === "malformed") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema version is rejected";
  // Hand-craft a receipt at a future schema version — same as iOS test.
  const json = JSON.stringify({
    schema: 99,
    messageId: "abc",
    type: "delivered",
    ts: 1,
  });
  const b64 = btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const payload = RECEIPT_PREFIX + b64;
  try {
    ReceiptCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof ReceiptCodecError &&
      e.tag.kind === "unsupportedSchema" &&
      e.tag.version === 99
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testEmptyMessageIdRejected(): Promise<TestCase> {
  const name = "empty messageId is rejected";
  const json = JSON.stringify({
    schema: 1,
    messageId: "",
    type: "delivered",
    ts: 1,
  });
  const b64 = btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const payload = RECEIPT_PREFIX + b64;
  try {
    ReceiptCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof ReceiptCodecError && e.tag.kind === "malformed") {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testTimestampIsPlaintext(): Promise<TestCase> {
  // The receipt body isn't separately signed — integrity comes from the outer
  // E2EE envelope. This test documents that contract: a receipt with ts=1
  // decodes fine and the ts value is preserved as-is.
  // Mirrors iOS testTimestampIsPlaintext.
  const name = "ts is plaintext-content (integrity is the envelope's job)";
  const r = makeReceipt("msg-x", "delivered", 1);
  const encoded = ReceiptCodec.encode(r);
  try {
    const decoded = ReceiptCodec.decode(encoded);
    const ok = decoded.ts === 1;
    return ok ? pass(name) : fail(name, `got ts=${decoded.ts}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// B. Store cases
// ---------------------------------------------------------------------------

async function testRecordDelivered(): Promise<TestCase> {
  const name = "record(delivered) marks user as delivered, not read";
  const s = freshStore();
  const r = makeReceipt("m1", "delivered");
  const result = await s.record(r, "alice");
  if (!result) return fail(name, "record returned null");
  const db = deliveredBy(result);
  const rb = readBy(result);
  const ok = db.has("alice") && db.size === 1 && rb.size === 0;
  return ok
    ? pass(name)
    : fail(name, `delivered=${[...db].sort()} read=${[...rb].sort()}`);
}

async function testReadUpgradesDelivered(): Promise<TestCase> {
  const name = "read upgrades delivered (and read implies delivered)";
  const s = freshStore();
  await s.record(makeReceipt("m1", "delivered"), "alice");
  const result = await s.record(makeReceipt("m1", "read"), "alice");
  if (!result) return fail(name, "record returned null");
  const rb = readBy(result);
  const db = deliveredBy(result);
  const ok = rb.has("alice") && db.has("alice");
  return ok
    ? pass(name)
    : fail(name, `delivered=${[...db].sort()} read=${[...rb].sort()}`);
}

async function testReadIsNotDowngradedByLateDelivered(): Promise<TestCase> {
  const name = "stale delivered does not downgrade read";
  const s = freshStore();
  await s.record(makeReceipt("m1", "read"), "alice");
  // Stale delivered arriving after read — must be discarded.
  const result = await s.record(makeReceipt("m1", "delivered"), "alice");
  // record() returns null for no-op discards.
  const after = await s.receipts("m1");
  const record = after.perUser["alice"];
  const ok = record?.highest === "read" && result === null;
  return ok
    ? pass(name)
    : fail(name, `highest=${record?.highest} returnedNull=${result === null}`);
}

async function testDuplicateReceiptIsIdempotent(): Promise<TestCase> {
  const name = "duplicate receipt does not churn observedAt";
  const s = freshStore();
  const r = makeReceipt("m1", "delivered", 100);
  const t1 = new Date(1000);   // epoch second 1
  const t2 = new Date(999000); // a later time
  await s.record(r, "alice", t1);
  const firstObserved = (await s.receipts("m1")).perUser["alice"]?.observedAt;
  // Second record with identical receipt but a later observedAt — should be discarded.
  const result2 = await s.record(r, "alice", t2);
  const secondObserved = (await s.receipts("m1")).perUser["alice"]?.observedAt;
  const ok =
    firstObserved !== undefined &&
    firstObserved === secondObserved &&
    firstObserved === t1.toISOString() &&
    result2 === null;
  return ok
    ? pass(name)
    : fail(name, `first=${firstObserved} second=${secondObserved}`);
}

async function testMultipleUsersTrackedIndependently(): Promise<TestCase> {
  const name = "multiple users tracked independently per message";
  const s = freshStore();
  await s.record(makeReceipt("m1", "delivered"), "alice");
  await s.record(makeReceipt("m1", "read"), "bob");
  await s.record(makeReceipt("m1", "delivered"), "carol");
  const r = await s.receipts("m1");
  const db = deliveredBy(r);
  const rb = readBy(r);
  const ok =
    db.has("alice") &&
    db.has("bob") &&
    db.has("carol") &&
    db.size === 3 &&
    rb.has("bob") &&
    rb.size === 1;
  return ok
    ? pass(name)
    : fail(name, `delivered=${[...db].sort()} read=${[...rb].sort()}`);
}

async function testClearOneMessageDoesNotAffectOthers(): Promise<TestCase> {
  const name = "clear(messageId) is scoped to one message";
  const s = freshStore();
  await s.record(makeReceipt("m1", "read"), "alice");
  await s.record(makeReceipt("m2", "read"), "bob");
  await s.clear("m1");
  const m1 = await s.receipts("m1");
  const m2 = await s.receipts("m2");
  const rb2 = readBy(m2);
  const ok = isEmptyMessageReceipts(m1) && rb2.has("bob") && rb2.size === 1;
  return ok
    ? pass(name)
    : fail(name, `m1.empty=${isEmptyMessageReceipts(m1)} m2.read=${[...rb2].sort()}`);
}

async function testPersistenceAcrossStoreInstances(): Promise<TestCase> {
  const name = "store state persists across instances";
  const uniqueDbName = `fr.kaether.receipts.test.${crypto.randomUUID()}`;
  const sharedIdb = createStore(uniqueDbName, "receipts");

  const writer = new ReceiptStore({
    idbStore: sharedIdb,
    toggleStorage: makeIsolatedToggleStorage(),
  });
  await writer.record(makeReceipt("m1", "read"), "alice");
  await writer.record(makeReceipt("m1", "delivered"), "bob");

  // Open a fresh ReceiptStore instance pointing at the same IDB store.
  const reader = new ReceiptStore({
    idbStore: sharedIdb,
    toggleStorage: makeIsolatedToggleStorage(),
  });
  const r = await reader.receipts("m1");
  const rb = readBy(r);
  const db = deliveredBy(r);
  const ok = rb.has("alice") && db.has("alice") && db.has("bob");
  return ok
    ? pass(name)
    : fail(name, `delivered=${[...db].sort()} read=${[...rb].sort()}`);
}

async function testReadReceiptToggleRoundTrips(): Promise<TestCase> {
  const name = "sendReadReceiptsEnabled round-trips through storage";
  const storage = makeIsolatedToggleStorage();
  const s = new ReceiptStore({
    idbStore: createStore(`fr.kaether.receipts.test.${crypto.randomUUID()}`, "receipts"),
    toggleStorage: storage,
  });
  const initial = s.sendReadReceiptsEnabled; // default ON
  s.sendReadReceiptsEnabled = false;
  const afterOff = s.sendReadReceiptsEnabled;
  s.sendReadReceiptsEnabled = true;
  const afterOn = s.sendReadReceiptsEnabled;

  // Re-construct a new store against the same storage to confirm persistence.
  const s2 = new ReceiptStore({
    idbStore: createStore(`fr.kaether.receipts.test.${crypto.randomUUID()}`, "receipts"),
    toggleStorage: storage,
  });
  const recovered = s2.sendReadReceiptsEnabled;

  const ok = initial === true && afterOff === false && afterOn === true && recovered === true;
  return ok
    ? pass(name)
    : fail(name, `initial=${initial} off=${afterOff} on=${afterOn} rec=${recovered}`);
}

async function testObservedAtIsLocalClock(): Promise<TestCase> {
  const name = "observedAt uses local clock, receiptTs preserves peer claim";
  const s = freshStore();
  const peerLies = 9_999_999_999;          // peer claims year 2286
  const weObserved = new Date(1_700_000_000_000); // our clock (ms)
  await s.record(
    makeReceipt("m1", "delivered", peerLies),
    "alice",
    weObserved,
  );
  const r = (await s.receipts("m1")).perUser["alice"];
  const ok =
    r !== undefined &&
    r.receiptTs === peerLies &&
    r.observedAt === weObserved.toISOString();
  return ok
    ? pass(name)
    : fail(name, `ts=${r?.receiptTs} observed=${r?.observedAt}`);
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Delivery & read receipts",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Codec
        testCodecRoundTripDelivered(),
        testCodecRoundTripRead(),
        testIsReceiptDetection(),
        testTryDecodeReturnsNullForUserMessage(),
        testMalformedBase64Rejected(),
        testUnsupportedSchemaRejected(),
        testEmptyMessageIdRejected(),
        testTimestampIsPlaintext(),
        // B. Store
        testRecordDelivered(),
        testReadUpgradesDelivered(),
        testReadIsNotDowngradedByLateDelivered(),
        testDuplicateReceiptIsIdempotent(),
        testMultipleUsersTrackedIndependently(),
        testClearOneMessageDoesNotAffectOthers(),
        testPersistenceAcrossStoreInstances(),
        testReadReceiptToggleRoundTrips(),
        testObservedAtIsLocalClock(),
      ]),
    ),
});
